import React, { Dispatch, SetStateAction } from "react";
import {
  ColumnDef,
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  RowSelectionState,
  SortingState,
  useReactTable,
  VisibilityState,
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { Button } from "./button";
import { Input } from "./input";
interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  containerClassname?: string;
  setRowSelection?: Dispatch<SetStateAction<RowSelectionState>>;
  rowSelection?: RowSelectionState;
  tableCaption?: string;
  Toolbar?: (props: { table: ReturnType<typeof useReactTable> }) => JSX.Element;
  tableMinWidth?: string;
}

function DataTable({
  columns,
  data,
  containerClassname,
  setRowSelection,
  rowSelection,
  tableCaption,
  Toolbar,
  tableMinWidth
}: DataTableProps<any, any>) {
  const [sorting, setSorting] = React.useState<SortingState>([]);

  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({});
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    []
  );
  // const [rowSelection, setRowSelection] = React.useState({});

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection ? setRowSelection : undefined,
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),

    state: {
      sorting,
      columnVisibility,
      rowSelection: rowSelection ? rowSelection : {},
      columnFilters: columnFilters ? columnFilters : undefined,
    },
  });

  return (
    <div className={`flex flex-col h-full w-full ${containerClassname}`}>
      <div className="flex flex-col overflow-auto">

        {/* Toolbar with sticky positioning */}

        {Toolbar && (
          <div className="sticky top-0 bg-white z-20 pb-2 w-full">
            <Toolbar table={table} />
          </div>
        )}

        {/* Table container with overflow */}
        <Table className="w-full overflow-x-auto" containerClassname={`min-w-[${tableMinWidth}]`}>
          {tableCaption && (
            <TableCaption className="sticky bottom-0 bg-white pt-2">
              {tableCaption}
            </TableCaption>
          )}
          <TableHeader className="sticky top-0 bg-white z-10">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id} className={`w-[${header.column.columnDef.size}px]`}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} className={`w-[${cell.column.columnDef.size}px]`}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export default DataTable;